/**
 * The master/detail pane for one component, as four tabs answering the four questions
 * an operator has about it (`component-deployment` spec, Master/detail):
 *
 *  - **Changes** — what this release changes in it, with the shared-dependency files
 *    separated from its own.
 *  - **Log** — the current or last run's output, live. The one place a compile that is
 *    taking twenty minutes can be watched, or a failure read.
 *  - **History** — the prior deploys, across releases, so "when did this last ship?"
 *    has an answer that does not depend on the current release.
 *  - **Config** — where it deploys to, which is what a guardrail rejection is about.
 *
 * The tab list is fixed rather than hiding empty ones: a tab that appears and
 * disappears as instructions run makes the panel move under the pointer, and "no log
 * yet" is itself the answer to "what is it doing?".
 */
import { useState } from 'react';
import {
    Alert,
    Chip,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import { DEPLOY_GROUP_RATIONALE, type ChangeSetPointer } from '@finvari/deployer';
import { DEPLOY_KIND_LABEL, compileOnlyExplanation, latestFor, type ComponentRow } from '../lib/componentRows';
import { agoTextCoarse } from '../lib/hostStatus';
import { archivePathFor, type InstructionRow } from '../lib/instructionActivity';
import { useComponentHistory } from '../hooks';
import { dbForRole } from '../firebase';
import { ChangeSetView } from './ChangeSetView';
import { FactList, type Fact } from './FactList';
import { LogViewer } from './LogViewer';

const TABS = ['Changes', 'Log', 'History', 'Config'] as const;

export interface ComponentDetailProps {
    row: ComponentRow;
    version: string;
    changes: ChangeSetPointer | undefined;
    /** This release's instructions on the release host, newest first. */
    instructions: InstructionRow[];
    now: number;
}

export function ComponentDetail({ row, version, changes, instructions, now }: ComponentDetailProps) {
    const [tab, setTab] = useState(0);
    return (
        <Stack spacing={2}>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                {TABS.map((label) => (
                    <Tab key={label} label={label} />
                ))}
            </Tabs>
            {tab === 0 && (
                <Stack spacing={2}>
                    <WhyInRelease row={row} />
                    <ChangeSetView pointer={changes} component={row.name} />
                </Stack>
            )}
            {tab === 1 && <ComponentLog row={row} instructions={instructions} />}
            {tab === 2 && <ComponentHistory row={row} version={version} now={now} />}
            {tab === 3 && <ComponentConfig row={row} />}
        </Stack>
    );
}

/**
 * Why a component with no changed files of its own is in the release at all.
 *
 * The change set above lists files; this answers the question those files provoke —
 * "what has `model/src/sutton/SuttonDailyDoc.ts` got to do with `receipts`?" — by
 * showing the import path the analysis followed to connect them. Shown for the
 * unaffected verdict too, since that is the claim behind a row saying no deploy is
 * needed and an operator deciding whether to trust it needs to see its basis.
 */
function WhyInRelease({ row }: { row: ComponentRow }) {
    const impact = row.impact;
    if (!impact || row.changeKind === 'none' || row.changeKind === 'direct') return null;

    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2">Why {row.name} is in this release</Typography>
            {impact.runtime === 'unaffected' ? (
                <Alert severity="info">{compileOnlyExplanation(row)}</Alert>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {impact.runtime === 'unknown'
                        ? 'The import graph could not be followed precisely here, so the whole of the changed package is assumed to be reachable.'
                        : `${impact.reachedFiles.length} changed ${impact.reachedFiles.length === 1 ? 'file is' : 'files are'} reachable from the code ${row.name} ships.`}
                </Typography>
            )}
            {impact.reasons.map((reason) => (
                <Stack key={reason.changedFile} spacing={0.25} sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                    <Typography variant="body2" fontFamily="monospace">
                        {reason.changedFile}
                    </Typography>
                    {reason.chain.map((step, i) => (
                        <Typography key={i} variant="caption" color="text.secondary" fontFamily="monospace">
                            {step.from} —{step.via}→ {step.to}
                        </Typography>
                    ))}
                </Stack>
            ))}
            {impact.fallbacks.map((fallback) => (
                <Typography key={fallback.reason} variant="caption" color="text.secondary">
                    {fallback.reason}
                </Typography>
            ))}
        </Stack>
    );
}

/**
 * The newest run for this component — compile, deploy or acknowledgement. Keyed on the
 * instruction id so switching rows rebuilds the terminal rather than appending one
 * component's output to another's.
 */
function ComponentLog({ row, instructions }: { row: ComponentRow; instructions: InstructionRow[] }) {
    const latest = latestFor(row.name, instructions);
    if (!latest) {
        return <Alert severity="info">Nothing has been run for {row.name} in this release yet.</Alert>;
    }
    return (
        <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
                {latest.row.kind} · {latest.row.status} · {latest.row.createdBy.email}
            </Typography>
            {latest.row.error && <Alert severity="error">{latest.row.error}</Alert>}
            {latest.row.rejectedReason && <Alert severity="warning">{latest.row.rejectedReason}</Alert>}
            <LogViewer
                key={latest.row.id}
                db={dbForRole('release')}
                instructionId={latest.row.id}
                archivePath={archivePathFor(latest.row, 'release')}
                live={latest.live}
            />
        </Stack>
    );
}

/**
 * Every deploy of this component, newest release first. Across releases on purpose:
 * the question this tab answers is "has this been shipping normally?", which one
 * release cannot say.
 */
function ComponentHistory({ row, version, now }: { row: ComponentRow; version: string; now: number }) {
    const { value: history, loading } = useComponentHistory(row.name);

    if (loading) {
        return (
            <Typography variant="body2" color="text.secondary">
                Loading…
            </Typography>
        );
    }
    if (history.length === 0) {
        return (
            <Alert severity="info">
                No deploy of {row.name} has been recorded here. Deploys run from a laptop before this system took over
                leave a marker in the checkout but no history.
            </Alert>
        );
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Release</TableCell>
                    <TableCell>When</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell>Result</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {history.map((h) => {
                    const at = h.at?.toMillis?.();
                    return (
                        <TableRow key={h.id} selected={h.version === version}>
                            <TableCell>{h.version}</TableCell>
                            <TableCell>{at === undefined ? '—' : agoTextCoarse(Math.max(0, now - at))}</TableCell>
                            <TableCell>{h.by.email}</TableCell>
                            <TableCell>{h.changeKind}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    color={h.result === 'succeeded' ? 'success' : 'error'}
                                    label={h.result}
                                />
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

/**
 * Where this component deploys and how. Read from the component document, which the
 * host denormalizes from the registry at every reconcile — so this is what the *host*
 * believes, which is the thing a guardrail rejection is about.
 */
function ComponentConfig({ row }: { row: ComponentRow }) {
    const facts: Fact[] = [
        { label: 'Path', value: row.path },
        { label: 'Deploy kind', value: DEPLOY_KIND_LABEL[row.deployKind] },
        { label: 'Project', value: row.expectedProject ?? 'not recorded — the host has not synced yet' },
        { label: 'Deploy group', value: `${row.deployGroup} — ${DEPLOY_GROUP_RATIONALE[row.deployGroup] ?? ''}` },
        { label: 'Compiles', value: row.compilable ? 'yes (pnpm run build)' : 'no build step' },
    ];
    if (row.hostingSite) facts.splice(3, 0, { label: 'Hosting site', value: row.hostingSite });
    if (row.deployMarker) {
        facts.push({
            label: 'deploy.log',
            value: `${row.deployMarker.commit} — ${row.deployMarker.at}${row.deployMarker.current ? ' (this release)' : ''}`,
        });
    }
    if (row.acknowledgement) {
        facts.push({
            label: 'Skipped',
            value: `${row.acknowledgement.reason} — ${row.acknowledgement.by.email}`,
        });
    }
    return <FactList facts={facts} />;
}
